// ---------------------------------------------------------------------------
// Provider performance table — success rate, latency, error counts
// ---------------------------------------------------------------------------

import { useFormatters } from '../hooks/useFormatters';

interface ProviderPerformanceRow {
  providerId: string;
  providerName: string;
  successRate: number;
  avgLatency: number;
  errorCount: number;
  lastError: string | null;
}

interface ProviderPerformanceTableProps {
  providers: ProviderPerformanceRow[];
}

function statusColor(successRate: number): { dot: string; bg: string; text: string } {
  if (successRate >= 99) return { dot: 'bg-primary-tint0', bg: 'bg-primary-tint', text: 'text-primary' };
  if (successRate >= 95) return { dot: 'bg-warning', bg: 'bg-warning-tint', text: 'text-warning' };
  return { dot: 'bg-danger-tint0', bg: 'bg-danger-tint', text: 'text-danger' };
}

function statusLabel(successRate: number): string {
  if (successRate >= 99) return 'Healthy';
  if (successRate >= 95) return 'Degraded';
  return 'Error';
}

function formatLatency(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function ProviderPerformanceTable({ providers }: ProviderPerformanceTableProps) {
  const f = useFormatters();
  return (
    <div className="bg-surface-raised rounded-sm shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-hairline">
        <h3 className="text-sm font-medium text-ink-muted uppercase tracking-wider">
          Provider Performance
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-hairline" aria-label="Provider performance">
          <thead className="bg-surface-sunken">
            <tr>
              <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-ink-muted uppercase tracking-wider">
                Provider
              </th>
              <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-ink-muted uppercase tracking-wider">
                Success Rate
              </th>
              <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-ink-muted uppercase tracking-wider">
                Avg Latency
              </th>
              <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-ink-muted uppercase tracking-wider">
                Errors
              </th>
              <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-ink-muted uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {providers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-ink-muted">
                  No provider data available
                </td>
              </tr>
            ) : (
              providers.map((p) => {
                const status = statusColor(p.successRate);
                return (
                  <tr key={p.providerId} className="hover:bg-surface-sunken">
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-ink">
                        {p.providerName}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-ink">
                        {p.successRate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-ink-muted">
                        {formatLatency(p.avgLatency)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-sm ${p.errorCount > 0 ? 'text-danger font-medium' : 'text-ink-muted'}`}>
                        {f.number(p.errorCount)}
                      </span>
                      {p.lastError && (
                        <p className="text-xs text-ink-muted mt-0.5 truncate max-w-xs" title={p.lastError}>
                          {p.lastError}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-sm text-xs font-medium ${status.bg} ${status.text}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-sm ${status.dot}`} />
                        {statusLabel(p.successRate)}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
